import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Input,
  Button,
  Space,
  message,
  DatePicker,
  Tabs,
  Divider,
} from "antd";
import { HomeOutlined, ProfileOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import { CustomUploadNew1 } from "@/hocComponents";
import commonFn from "@/utils/commonFn";

const VendorModal = forwardRef(
  ({ submitInitFn, open, onCancel, onSuccess }, ref) => {
    const [form] = Form.useForm();
    const [modalFlag, setModalFlag] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [filePaths, setFilePaths] = useState([]);
    const customUploadNewRef = useRef([]);
    const [groupIdList, setGroupIdList] = useState([]);
    const [propIdList, setPropIdList] = useState([]);
    const [tabsKey, setTabsKey] = useState("w9");
    const [vendorTypes, setVendorTypes] = useState([]);
    const [states, setStates] = useState([]);
    const [glAcctTypes, setGlAcctTypes] = useState([]);
    const [glAcctLoading, setGlAcctLoading] = useState(false);

    useImperativeHandle(ref, () => ({
      openModal: () => {
        setModalFlag(true);
      },
    }));

    useEffect(() => {
      if (typeof open === "boolean") {
        setModalFlag(open);
      }
    }, [open]);

    const closeModal = () => {
      setModalFlag(false);
      onCancel?.();
    };

    const afterOpenChangeFn = (flag) => {
      if (flag) {
        fetchInitialData();
        groupIdListFn();
        propIdListFn();
      } else {
        setTabsKey("w9");
        form.resetFields();
        setBtnLoading(false);
        setBtnDisabled(false);
        setFilePaths([]);
        setVendorTypes([]);
        setGroupIdList([]);
        setPropIdList([]);
        setStates([]);
        setGlAcctTypes([]);
      }
    };
    const isIfList = [
      { value: "Y", label: "Yes" },
      { value: "N", label: "No" },
    ];

    const isACHList = [
      { value: 1, label: "Yes" },
      { value: 0, label: "No" },
    ];

    const optionalUsPhoneRule = {
      validator: (_, value) => {
        const trimmedValue = String(value ?? "").trim();
        if (!trimmedValue) return Promise.resolve();
        const usPhoneRegex =
          /^(\+1[\s.-]?)?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/;
        return usPhoneRegex.test(trimmedValue)
          ? Promise.resolve()
          : Promise.reject(
              new Error(
                "Please enter a valid US phone number (e.g. 123-456-7890).",
              ),
            );
      },
    };

    const submitAddFn = async () => {
      const formValues = form.getFieldValue();
      const hireDate = formValues?.hireDate?.format?.("YYYY-MM-DD") || undefined;
      setBtnLoading(true);
      const response = await http.post(api.getVendorAdd, {
        ...formValues,
        hireDate,
        uuidList: filePaths.map((item) => item.uuid),
      });
      if (response.code === "1") {
        message.success("New successfully");
        closeModal();
        submitInitFn?.();
        onSuccess?.();
      } else {
        commonFn.modalFailedFn(response);
      }
      setBtnLoading(false);
    };

    const submitFn = async () => {
      form
        .validateFields()
        .then(() => {
          submitAddFn();
        })
        .catch((err) => {
          console.log(err, "===err");
        });
    };

    const deleteOldFn = async (file) => {
      let flag = false;
      const response = await http.get(api.commonDelFiles, {
        params: { id: file.id },
      });
      if (response.code === "1") {
        flag = true;
      }
      return flag;
    };

    const deleteBase64FileFn = async (file) => {
      const list = filePaths.filter((item) =>
        file?.uuid ? item.uuid !== file.uuid : item.uid !== file.uid,
      );
      return list;
    };

    const groupIdListFn = async (content = "") => {
      const response = await http.get(api.commonGroupList, {
        params: { content },
      });
      if (response.code === "1") {
        setGroupIdList(response.data || []);
      }
    };

    const propIdListFn = async (content = "") => {
      const response = await http.get(api.propSearch, { params: { content } });
      if (response.code === "1") {
        setPropIdList(response.data || []);
      }
    };

    const fetchInitialData = async () => {
      setGlAcctLoading(true);
      try {
        const [vendorTypesResponse, statesResponse, glAcctResponse] =
          await Promise.all([
            http.get(api.getVendorType, { params: { flag: "status" } }),
            http.get(api.getVendorType, { params: { flag: "state" } }),
            http.get(api.getVendorType, { params: { flag: "glAcct" } }),
          ]);

        setVendorTypes(
          (vendorTypesResponse || []).map((item, index) => ({
            value: item.typeCode,
            label: item.typeName,
            key: `${item.typeCode}-${index}`,
          })),
        );

        const stateOptions = (statesResponse || []).map((item, index) => ({
          value: item.stateM,
          label: item.stateName,
          key: `${item.stateM}-${index}`,
        }));
        setStates(stateOptions);

        setGlAcctTypes(
          (glAcctResponse || []).map((item, index) => ({
            value: item.glAcct,
            label: item.glAcctName,
            key: `${item.glAcct}-${index}`,
          })),
        );
      } catch (error) {
        console.error("Failed to load vendor dropdown data:", error);
      } finally {
        setGlAcctLoading(false);
      }
    };

    const addOldFn = async (file, type) => {
      setBtnDisabled(true);
      const list = file.filter((item) => !item.uuid && !item.id);
      const uploadedFiles = await commonFn.uploadFiles(list, {
        type,
        foreignId: "q1FJeKGXg6l9DKvolVahqXl2477uRIwqcj9lNfRK",
      });
      const temp = uploadedFiles.reduce((arr, u) => {
        const f = (list || []).find((v) => v.uid === u.customId);
        if (f) {
          const { originFileObj, allPath, size, type } = f;
          arr.push({
            ...u,
            originFileObj,
            allPath:
              allPath || (originFileObj && URL.createObjectURL(originFileObj)),
            size,
            type,
          });
        }
        return arr;
      }, []);
      return {
        flag: true,
        fileData: temp[0],
      };
    };

    const uploadTabsChildrenDoc = (name) => {
      return (
        <Form.Item
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}
          name={name}
          label={null}
        >
          <>
            <div style={{ height: "50px" }}></div>
            <CustomUploadNew1
              ref={(el) => (customUploadNewRef.current[name] = el)}
              customConfig={{
                uploadButtonStyle: {
                  top: "0",
                },
                pdfHeight: "62vh",
                fileFullName: "name",
                notMultiple: true,
                accept: ".jpg, .jpeg, .png, .pdf",
                status: "add",
                deleteOldFn: deleteOldFn,
                deleteBase64FileFn: deleteBase64FileFn,
                addOldFn: (file) => addOldFn(file, tabsKey),
              }}
              setFilePaths={setFilePaths}
              filePaths={filePaths.filter((item) => item.customType === name)}
              setBtnDisabled={setBtnDisabled}
              btnDisabled={btnDisabled}
            />
          </>
        </Form.Item>
      );
    };

    const uploadTabsDoc = () => {
      const items = [
        {
          key: "w9",
          label: "Upload W-9 Documents",
          forceRender: true,
          children: uploadTabsChildrenDoc("w9"),
        },
        {
          key: "insurance_certificate",
          label: "Upload Insurance Certificate",
          forceRender: true,
          children: uploadTabsChildrenDoc("insurance_certificate"),
        },
        {
          key: "contracts",
          label: "Upload Contracts",
          forceRender: true,
          children: uploadTabsChildrenDoc("contracts"),
        },
      ];
      return (
        <Tabs
          activeKey={tabsKey}
          items={items}
          onChange={(key) => setTabsKey(key)}
        />
      );
    };

    return (
      <Modal
        title={
          <div
            style={{ fontSize: "24px", fontWeight: "bold", color: "#3c8dbc" }}
          >
            <HomeOutlined style={{ marginRight: "8px" }} />
            New Vendor
            <div
              style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
            ></div>
          </div>
        }
        className="VendorMWorkerModal"
        centered
        open={modalFlag}
        onCancel={closeModal}
        footer={null}
        maskClosable={false}
        afterOpenChange={afterOpenChangeFn}
        width="80%"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          labelWrap={true}
          labelAlign="right"
          initialValues={{
            flg1099: "N",
          }}
          validateMessages={{
            required: "${label} is required!",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <ProfileOutlined style={{ marginRight: "8px" }} />
                Service Provides
              </div>
              <Form.Item
                name="vendid"
                label="Vendor Code"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="name"
                label="Vendor Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="vendorType"
                label="Vendor Type"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Please Select Vendor Type"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={vendorTypes}
                />
              </Form.Item>
      
              <Form.Item name="hireDate" label="Hire Date">
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  picker="date"
                />
              </Form.Item>
              {/* {commonFn.formInputNumberDoc({
                form,
                name: "monthlySalary",
                label: "Monthly Salary",
                allowNegative: true,
                isNoRequired: true,
              })} */}
              <Form.Item name="remarks" label="Remark">
                <Input placeholder="Please input Remark" />
              </Form.Item>
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <ProfileOutlined style={{ marginRight: "8px" }} />
                Mailing Address (This will print on check.)
              </div>
              <Form.Item
                name="street"
                label="Address"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="city" label="City" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item
                name="state"
                label="State"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="State"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={states}
                />
              </Form.Item>
              <Form.Item
                name="zip"
                label="Zip Code"
                 rules={[
                                  { required: true, message: "Zip Code is required" },
                                  { validator: commonFn.zipValidator },
                                ]}
                              >
                <Input />
              </Form.Item>
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <ProfileOutlined style={{ marginRight: "8px" }} />
                Physical Address
              </div>
              <Form.Item name="streetM" label="Address(P.O. Box)">
                <Input />
              </Form.Item>

              <Form.Item name="cityM" label="City">
                <Input />
              </Form.Item>

              <Form.Item name="stateM" label="State">
                <Select
                  showSearch
                  allowClear
                  placeholder="State"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={states}
                />
              </Form.Item>

              <Form.Item name="zipM" label="Zip Code"  rules={[{ validator: commonFn.zipValidator }]}>
                <Input />
              </Form.Item>

              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <ProfileOutlined style={{ marginRight: "8px" }} />
                Overnight Mailing Info
              </div>
              <Form.Item name="streetO" label="Address">
                <Input />
              </Form.Item>

              <Form.Item name="cityO" label="City">
                <Input />
              </Form.Item>

              <Form.Item name="stateO" label="State">
                <Select
                  showSearch
                  allowClear
                  placeholder="State"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={states}
                />
              </Form.Item>

              <Form.Item name="zipO" label="Zip Code"  rules={[{ validator: commonFn.zipValidator }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <ProfileOutlined style={{ marginRight: "8px" }} /> Contact Info
              </div>

              <Form.Item name="nameKey" label="Name">
                <Input />
              </Form.Item>

              <Form.Item
                name="phoneWork"
                label="Work"
                rules={[{ validator: commonFn.usPhoneValidator }]}>
           
                <Input
                  placeholder="e.g. (123) 456-7890"
                />
              </Form.Item>

              <Form.Item name="ext" label="Extension">
                <Input />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Mobile"
              rules={[{ validator: commonFn.usPhoneValidator }]}>
                <Input
                  placeholder="e.g. (123) 456-7890"
                />
              </Form.Item>

              <Form.Item
                name="fax"
                label="Fax"
              rules={[{ validator: commonFn.usPhoneValidator }]}>
                <Input
                  placeholder="e.g. (123) 456-7890"
                />
              </Form.Item>

              <Form.Item name="email" label="Email">
                <Input />
              </Form.Item>
              <Form.Item name="web" label="Website">
                <Input />
              </Form.Item>
              <Divider />
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <ProfileOutlined style={{ marginRight: "8px" }} /> Payment Info
              </div>
              <Form.Item
                name="flg1099"
                label="If 1099"
                rules={[{ required: true }]}
              >
                <Select
                  placeholder="Select "
                  options={isIfList}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="fedId" label="Federal ID/SSN">
                <Input />
              </Form.Item>
              <Form.Item name="ach" label="ACH payment?">
                <Select
                  placeholder="Select Group"
                  options={isACHList}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="routingNumber" label="Routing Number">
                <Input />
              </Form.Item>
              <Form.Item name="accountNumber" label="Account Number">
                <Input />
              </Form.Item>

              <Form.Item name="glAcct" label="Default GL Account">
                <Select
                  placeholder="Select "
                  options={glAcctTypes}
                  loading={glAcctLoading}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                <HomeOutlined style={{ marginRight: "8px" }} />
                Upload PDF image
              </div>

              <div className="uploadText">
                Uploaded files only support JPG, PNG or PDF.
              </div>
              {uploadTabsDoc()}
            </Col>
          </Row>
        </Form>
        <div style={{ textAlign: "center" }}>
          <Space align="center">
            {/* <Button key="back" onClick={testFn}>
              TEST
            </Button> */}
            <Button key="back" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={submitFn}
              loading={btnLoading}
              disabled={btnDisabled}
            >
              Submit
            </Button>
          </Space>
        </div>
      </Modal>
    );
  },
);

export default VendorModal;
